import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect } from 'react-router-dom';
import { Box } from 'reflexbox';

import * as graphql from '@treams/treams-network';
import {
	AddIcon,
	Button,
	CashIcon,
	Container,
	Empty,
	FlexGap,
	Heading4,
	Jumbo,
	Spinner,
	StarsIcon,
	Subheading,
	tokens,
} from '@treams/treams-ui';

import { useRightSection } from '../layout/rightSectionContext';
import CreateSalaryChangeModal from './CreateSalaryChangeModal';
import SalaryChangeRow from './SalaryChangeRow';
import { formatSalaryAtFte } from './formatSalary';

export default function SalaryDevelopment(props: { userID: string }) {
	const { t, i18n } = useTranslation();
	const [open, setOpen] = useState(false);
	const rightSection = useRightSection();
	const query = graphql.useSalaryChangesQuery({
		variables: { id: props.userID },
		fetchPolicy: 'network-only',
	});

	if (!query.data) {
		return <Spinner centered />;
	}

	const user = query.data.user;
	if (!user.viewerCanViewSalaryChanges) {
		return <Redirect to={`/users/${props.userID}`} />;
	}

	const changes = user.salaryChanges;
	const current = changes.find(
		(change) => change.reason !== graphql.SalaryChangeReason.OneOffBonus,
	);

	return (
		<>
			<Jumbo
				title={t('salaryDevelopment.title')}
				button={
					<FlexGap gap={tokens.gap.element}>
						{rightSection?.hasAi && (
							<Button
								type="secondary"
								role="button"
								aria-label={t('salaryDevelopment.explainTips')}
								title={t('salaryDevelopment.explainTips')}
								icon={<StarsIcon />}
								onClick={() =>
									rightSection.openAi({
										prompt: t('salaryDevelopment.explainPrompt'),
										subjectID: props.userID,
									})
								}
							/>
						)}
						<Button
							role="button"
							aria-label={t('salaryDevelopment.add')}
							title={t('salaryDevelopment.add')}
							icon={<AddIcon />}
							onClick={() => setOpen(true)}
						/>
					</FlexGap>
				}
			/>
			<Container pt={`${tokens.gap.page}px`} pb={4}>
				{changes.length === 0 ? (
					<Empty.Card
						icon={<CashIcon />}
						title={t('salaryDevelopment.empty.title')}
						description={t('salaryDevelopment.empty.description')}
					/>
				) : (
					<FlexGap flexDirection="column" gap={tokens.gap.section}>
						{current && (
							<FlexGap flexDirection="column" gap={tokens.gap.inline}>
								<Heading4>{t('salaryDevelopment.current')}</Heading4>
								<Subheading>
									{formatSalaryAtFte(current.amount, current.fteHours, i18n.language, (values) =>
										t('salaryDevelopment.fteHours.atFullTime', values),
									)}
								</Subheading>
							</FlexGap>
						)}
						<Box>
							{changes.map((change) => (
								<SalaryChangeRow key={change.id} change={change} />
							))}
						</Box>
					</FlexGap>
				)}
			</Container>
			{open && (
				<CreateSalaryChangeModal
					userID={props.userID}
					onClose={() => setOpen(false)}
					onCreated={() => query.refetch()}
				/>
			)}
		</>
	);
}
